import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CalendarDays, X } from 'lucide-react';
import { DanishCalendarMonth } from '@/components/ui/DanishCalendarMonth';
import { Input } from '@/components/ui/Input';
import { formatDanishCivilDate, parseDanishCivilDate } from '@/lib/danishDate';
import { todayCivil } from '@/lib/questionnaireDateFormat';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const CIVIL = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Portaled to document.body. Appointment slide-overs are z-40, so 80 cleared
 * those. The file preview (`.fp-viewer`, z-index 100) covers the viewport, and
 * a popover at 80 opened behind Markering, so the calendar icon looked dead.
 * 110 matches the file header menus and stays under file-preview confirms (120).
 */
const DATE_POPOVER_Z_INDEX = 110;

function monthOf(civil: string): { year: number; monthIndex: number } {
  return { year: Number(civil.slice(0, 4)), monthIndex: Number(civil.slice(5, 7)) - 1 };
}

function shiftView(
  year: number,
  monthIndex: number,
  delta: number,
): { year: number; monthIndex: number } {
  const next = new Date(Date.UTC(year, monthIndex + delta, 1));
  return { year: next.getUTCFullYear(), monthIndex: next.getUTCMonth() };
}

export function DanishDateField({
  value,
  onChange,
  label,
  name,
  disabled,
  required,
  calendar = false,
}: {
  value: string;
  onChange: (civil: string) => void;
  label?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  /** Calendar icon that opens a Monday-first popover. Month and weekday names stay Danish. */
  calendar?: boolean;
}) {
  const { t } = useTranslation();
  const dialogId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState(() => formatDanishCivilDate(value) || value);
  const [invalid, setInvalid] = useState(false);
  const [open, setOpen] = useState(false);
  const selectedCivil = parseDanishCivilDate(text) || (CIVIL.test(value) ? value : '');
  const viewSource = selectedCivil || todayCivil();
  const [view, setView] = useState(() => monthOf(viewSource));
  const viewSourceRef = useRef(viewSource);

  useEffect(() => {
    setText(formatDanishCivilDate(value) || value);
    setInvalid(false);
  }, [value]);

  useEffect(() => {
    if (viewSourceRef.current === viewSource) return;
    viewSourceRef.current = viewSource;
    setView(monthOf(viewSource));
  }, [viewSource]);

  useEffect(() => {
    if (!open) return;
    function onPointer(event: MouseEvent) {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (dialogRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const dialog = dialogRef.current;
    if (!anchor || !dialog) return;
    function place() {
      if (!anchor || !dialog) return;
      const rect = anchor.getBoundingClientRect();
      const width = dialog.offsetWidth || 280;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      dialog.style.top = `${rect.bottom + 4}px`;
      dialog.style.left = `${left}px`;
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, view.year, view.monthIndex]);

  function commit(next: string) {
    const parsed = parseDanishCivilDate(next);
    if (!next.trim()) {
      setInvalid(false);
      onChange('');
      setText('');
      return;
    }
    if (!parsed) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onChange(parsed);
    setText(formatDanishCivilDate(parsed));
  }

  function pick(civil: string) {
    setInvalid(false);
    if (!civil) {
      onChange('');
      setText('');
    } else {
      onChange(civil);
      setText(formatDanishCivilDate(civil));
    }
    setOpen(false);
  }

  // `calendar` keeps dd-mm-åååå and a Danish popover when the app language is English.
  const control = (
    <Input
      name={name}
      label={label}
      value={text}
      disabled={disabled}
      required={required}
      inputMode="numeric"
      autoComplete="off"
      placeholder={calendar ? 'dd-mm-åååå' : t('common.date_placeholder')}
      hint={calendar ? 'dd-mm-åååå' : t('common.date_format_hint')}
      error={
        invalid ? (calendar ? 'Skriv datoen som dd-mm-åååå' : t('common.date_invalid')) : undefined
      }
      trailingIcon={
        calendar ? (
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-ds-muted hover:text-ds-text focus-visible:shadow-ring focus-visible:outline-none disabled:opacity-40"
            data-testid="danish-date-calendar"
            aria-label={t('common.open_date_calendar')}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={dialogId}
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setOpen((current) => !current)}
          >
            <CalendarDays className="h-4 w-4" aria-hidden />
          </button>
        ) : undefined
      }
      onChange={(event) => {
        setText(event.target.value);
        setInvalid(false);
      }}
      onBlur={(event) => commit(event.target.value)}
    />
  );

  if (!calendar) return control;

  return (
    <div ref={anchorRef} className="relative">
      {control}
      {open
        ? createPortal(
            <div
              ref={dialogRef}
              id={dialogId}
              className="calp fixed w-[17.5rem]"
              style={{ zIndex: DATE_POPOVER_Z_INDEX }}
              role="dialog"
              lang="da"
              data-testid="danish-date-calendar-popover"
              aria-label={t('common.date_calendar')}
            >
              {/* Always da: Sendt must stay Danish when the app language is English. */}
              <DanishCalendarMonth
                year={view.year}
                monthIndex={view.monthIndex}
                language="da"
                value={selectedCivil}
                onShift={(delta) =>
                  setView((current) => shiftView(current.year, current.monthIndex, delta))
                }
                onSelect={pick}
              />
              {selectedCivil ? (
                <button
                  type="button"
                  className={cn(
                    TYPE.small,
                    'mt-2 inline-flex items-center gap-1 font-semibold text-ds-teal',
                  )}
                  onClick={() => pick('')}
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                  Ryd
                </button>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
